import { useCallback, useEffect, useState } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import { ProfileResource } from '@medplum/core';
import { Encounter } from '@medplum/fhirtypes';

export const useMyInProgressEncountersCount = (
  profile?: ProfileResource,
  canManageEncounters?: boolean
) => {
  const medplum = useMedplum();
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchCount = useCallback(async () => {
    if (!profile?.id || !canManageEncounters) {
      setCount(0);
      return;
    }

    setLoading(true);
    try {
      const result = await medplum.search('Encounter', {
        participant: `Practitioner/${profile.id}`,
        status: 'in-progress',
        _summary: 'count',
      });
      setCount(result.total || 0);
    } catch (error) {
      console.error('Error fetching in-progress encounter count:', error);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [medplum, profile?.id, canManageEncounters]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Subscribe to encounter changes for real-time count updates
  useSubscription(
    profile?.id && canManageEncounters
      ? `Encounter?participant=Practitioner/${profile.id}`
      : '',
    (bundle) => {
      const encounter = bundle.entry?.find(
        (e) => e.resource?.resourceType === 'Encounter'
      )?.resource as Encounter | undefined;

      if (encounter) {
        // Refetch count when encounter status might have changed
        fetchCount();
      }
    }
  );

  return { count, loading, refetch: fetchCount };
};
